/**
 * Plain-language delivery outcomes (D-38). The API classifies every delivery into an issue kind;
 * these components show it as a labelled chip and as an explanation with a suggested action.
 */
import type { DeliveryIssue } from "@aiyo/shared";
import {
  ArrowsRotateRight,
  CircleCheck,
  CircleExclamation,
  CircleXmark,
  Clock,
  PaperPlane,
} from "@gravity-ui/icons";
import { Chip } from "@heroui/react";
import type { ComponentType, SVGProps } from "react";
import { useTranslation } from "react-i18next";

export type IssueTone = "success" | "accent" | "default" | "warning" | "danger";

const TONE: Record<DeliveryIssue, IssueTone> = {
  delivered: "success",
  accepted: "success",
  in_progress: "default",
  retrying: "warning",
  customer_not_found: "warning",
  no_subscription: "warning",
  subscription_invalid: "warning",
  unsubscribed: "default",
  unknown_outcome: "warning",
  address_invalid: "danger",
  token_wrong_project: "danger",
  credential_problem: "danger",
  provider_unavailable: "danger",
  payload_rejected: "danger",
  bounced: "danger",
  other: "danger",
};

export function issueTone(issue: DeliveryIssue): IssueTone {
  return TONE[issue];
}

const ICON: Record<IssueTone, ComponentType<SVGProps<SVGSVGElement>>> = {
  success: CircleCheck,
  accent: PaperPlane,
  default: Clock,
  warning: CircleExclamation,
  danger: CircleXmark,
};

function iconFor(issue: DeliveryIssue) {
  if (issue === "accepted") return PaperPlane;
  if (issue === "retrying") return ArrowsRotateRight;
  return ICON[TONE[issue]];
}

/** Status pill: colour, icon and label together, so the colour never carries meaning alone. */
export function IssueChip({ issue }: { issue: DeliveryIssue }) {
  const { t } = useTranslation();
  const Icon = iconFor(issue);
  return (
    <Chip size="sm" variant="soft" color={TONE[issue]} className="gap-1 whitespace-nowrap">
      <Icon className="size-3.5" aria-hidden />
      {t(`issues.${issue}.label`)}
    </Chip>
  );
}

const PANEL: Record<IssueTone, string> = {
  success: "bg-success-soft text-success-soft-foreground",
  accent: "bg-accent-soft text-accent-soft-foreground",
  default: "bg-default text-foreground",
  warning: "bg-warning-soft text-warning-soft-foreground",
  danger: "bg-danger-soft text-danger-soft-foreground",
};

/** "What happened" and "What to do" for one delivery. */
export function IssueExplanation({ issue }: { issue: DeliveryIssue }) {
  const { t } = useTranslation();
  const tone = TONE[issue];
  const Icon = iconFor(issue);
  return (
    <section className={`flex gap-3 rounded-2xl p-4 ${PANEL[tone]}`} aria-label={t("delivery.whatHappened")}>
      <Icon className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-col gap-3 text-sm leading-relaxed">
        <div className="flex flex-col gap-1">
          <h3 className="font-semibold">{t(`issues.${issue}.label`)}</h3>
          <p>{t(`issues.${issue}.explain`)}</p>
        </div>
        <div className="flex flex-col gap-1">
          <h4 className="text-xs font-semibold tracking-wide uppercase opacity-80">
            {t("delivery.whatToDo")}
          </h4>
          <p>{t(`issues.${issue}.action`)}</p>
        </div>
      </div>
    </section>
  );
}
